"use client"

import { FormEvent, useState } from "react"

import { Button } from "@medusajs/ui"

import { API_BASE_URL } from "@lib/config"
import type { StorefrontComponentsNewsletter } from "@lib/data/storefront-components"
import { readJsonResponse } from "@lib/http/read-json-response"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i

type NewsletterProps = {
  settings?: StorefrontComponentsNewsletter | null
}

const Newsletter = ({ settings }: NewsletterProps) => {
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle")
  const [message, setMessage] = useState<string | null>(null)

  const backgroundColor =
    settings?.backgroundColor || "var(--storefront-newsletter-bg, #000)"
  const textColor =
    settings?.textColor || "var(--storefront-newsletter-text, #fff)"
  const inputBackgroundColor =
    settings?.inputBackgroundColor ||
    "var(--storefront-newsletter-input-bg, #fff)"
  const inputTextColor =
    settings?.inputTextColor || "var(--storefront-newsletter-input-text, #000)"
  const inputBorderColor =
    settings?.inputBorderColor ||
    "var(--storefront-newsletter-input-border, #fff)"
  const buttonBackgroundColor =
    settings?.buttonBackgroundColor ||
    "var(--storefront-newsletter-button-bg, #000)"
  const buttonTextColor =
    settings?.buttonTextColor ||
    "var(--storefront-newsletter-button-text, #fff)"
  const buttonBorderColor =
    settings?.buttonBorderColor ||
    "var(--storefront-newsletter-button-border, #fff)"

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setMessage(null)

    const normalizedEmail = email.trim()
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setStatus("error")
      setMessage("Ingresá un correo electrónico válido.")
      return
    }

    setStatus("loading")

    const fallbackError = "No pudimos registrar tu correo. Intenta nuevamente."

    try {
      const response = await fetch(
        `${API_BASE_URL}/public/newsletter/subscribers`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: normalizedEmail }),
        }
      )

      const data = await readJsonResponse<{
        error?: string
        alreadySubscribed?: boolean
      }>(response, { context: "newsletter", optional: true })

      if (!response.ok) {
        const errorMessage = data?.error || fallbackError
        throw new Error(errorMessage)
      }

      setStatus("success")
      if (data?.alreadySubscribed) {
        setMessage(
          "Este correo ya está suscripto. ¡Gracias por seguir con nosotros!"
        )
      } else {
        setMessage(
          "¡Gracias por suscribirte! Pronto recibirás nuestras novedades."
        )
      }
      setEmail("")
    } catch (error) {
      setStatus("error")
      setMessage(error instanceof Error ? error.message : fallbackError)
    }
  }

  const isLoading = status === "loading"
  const isSuccess = status === "success"

  return (
    <div
      className="content-container h-[45vh] w-full bg-cover bg-center bg-no-repeat relative"
      style={{
        backgroundColor,
        color: textColor,
      }}
    >
      <div className="absolute inset-0 flex items-center z-20">
        <div className="flex flex-col max-w-4xl px-8 lg:px-16 gap-2">
          <h1
            className="text-lg md:text-4xl font-lealti-light"
            style={{ color: textColor }}
          >
            Recibí descuentos exclusivos en tu correo
          </h1>
          <form
            className="flex md:mt-4  md:w-full max-w-xl flex-row md:flex-row sm:items-center"
            onSubmit={handleSubmit}
            noValidate
          >
            <div className="flex">
              <input
                type="email"
                inputMode="email"
                placeholder="Tu correo electrónico"
                className="newsletter-input font-lealti-light"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                aria-label="Correo electrónico"
                disabled={isLoading}
                style={{
                  backgroundColor: inputBackgroundColor,
                  color: inputTextColor,
                  borderColor: inputBorderColor,
                }}
              />
            </div>
            <div className="flex">
              <Button
                type="submit"
                variant="secondary"
                size="large"
                disabled={isLoading}
                className="px-5 py-4 text-md border-2 bg-lealti border-lealti font-lealti-light rounded-none shadow-none outline-none flex gap-2  text-white h-[50px]"
                style={{
                  backgroundColor: buttonBackgroundColor,
                  color: buttonTextColor,
                  borderColor: buttonBorderColor,
                }}
              >
                {isLoading ? "Enviando..." : "Suscribirme"}
              </Button>
            </div>
          </form>

          <div className="mt-2 min-h-[1.25rem]" aria-live="polite">
            {message && (
              <p
                className={`text-sm ${
                  isSuccess ? "text-green-700" : "text-red-600"
                }`}
              >
                {message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Newsletter
